﻿import { h, Fragment, Component, render } from 'preact';
import styles from './KPIVisualiser.module.scss';
import KpiVisualiserContext from './KPIVisualiser_Context';
import { KpiVisualiser_Entry } from './KPIVisualiser_Entry';
import { KpiVisualiser_Entry_LeftHeader } from './KPIVisualiser_Entry_LeftHeader';
import { KpiVisualiser_Grid_Lines, KpiVisualiser_Grid_Top } from './KPIVisualiser_Grid';
import { KpiVisualiser_Key } from './KPIVisualiser_Key';
import { KpiVisualiser_TrackerLine } from './KPIVisualiser_TrackerLine';
import { DAYS_TO_MS, DAY_WIDTH, dateObjToUtcDate, dateToXPos, GRID_MARGIN_TOP, LEFT_HEADER_WIDTH, entryIndexToYPos, ENTRY_PADDING, SvgCommonDefs, DAY_OF_WEEK_TO_NUM_MAP } from './Utils';

interface Props {
    domElement: KPIVisualiserDOM;
}

interface State {
    scrollX: number;
    scrollY: number;
    zoom: number;
    mouseDown: boolean;
    controlsVisible: boolean;
    keyVisible: boolean;
}

const INITIAL_SCROLL_OFFSET = DAY_WIDTH * 5.0;
const MIN_ZOOM_LEVEL = 0.3;
const MAX_ZOOM_LEVEL = 4.0;
const REFRESH_DATA_RATE_MS = 60 * 1000;

// A React class to simplify the generation of the SVG
export class KpiVisualiser extends Component<Props, State> {
    public static instance: KpiVisualiser;
    private static refreshTimer: any;

    public static DotNetInit(pageRef: DotNet.DotNetObject) {
        KpiVisualiserContext.pageReference = pageRef;
        KpiVisualiser.refreshTimer = setInterval(() => {
            if (KpiVisualiserContext.pageReference) {
                console.debug("Refreshing KPI definitions");
                KpiVisualiserContext.pageReference.invokeMethodAsync("JS_RefreshKpis");
            } else {
                clearInterval(KpiVisualiser.refreshTimer);
            }
        }, REFRESH_DATA_RATE_MS);
    }

    public static DotNetDispose() {
        KpiVisualiserContext.pageReference = null;
        clearInterval(KpiVisualiser.refreshTimer);
    }

    public static SetKpiEntries(newEntries: KPIRuntime[]) {
        KpiVisualiserContext.entries = newEntries;
    }

    public static SetWorkingHours(workingHours: (WorkingHour | { dayOfWeek: string })[]) {
        for (const w of workingHours) {
            if (typeof w.dayOfWeek === 'string') {
                w.dayOfWeek = DAY_OF_WEEK_TO_NUM_MAP[w.dayOfWeek.toLowerCase()];
            }
        }

        KpiVisualiserContext.workingHours = workingHours as WorkingHour[];
    }

    public static SetSelectedEntry(newID: string | null) {
        KpiVisualiserContext.selectedEntry = newID;
        if (KpiVisualiserContext.selectedEntry) {
            console.log(`Selected KPI Runtime with ID '${KpiVisualiserContext.selectedEntry}'`);
        } else {
            console.log(`Deselected KPI Runtime`);
        }

        if (KpiVisualiser.instance) {
            // Force a rerender
            KpiVisualiser.instance.setState({});
        }
    }

    public static GoToSelectedRuntime() {
        if (KpiVisualiser.instance) {
            KpiVisualiser.instance.goToSelectedRuntime();
        }
    }

    public static ReturnToFirstRuntime() {
        if (KpiVisualiser.instance) {
            KpiVisualiser.instance.returnToFirstRuntime();
        }
    }

    public static ReturnToCurrentTime() {
        if (KpiVisualiser.instance) {
            KpiVisualiser.instance.returnToCurrentTime();
        }
    }

    state = {
        scrollX: INITIAL_SCROLL_OFFSET,
        scrollY: 0,
        zoom: 1.0,
        mouseDown: false,
        controlsVisible: false,
        keyVisible: false,
    } as State;

    componentDidMount() {
        KpiVisualiser.instance = this;
    }

    goToSelectedRuntime() {
        if (KpiVisualiserContext.entries.length == 0 || !KpiVisualiserContext.selectedEntry) {
            this.returnToCurrentTime();
            return;
        }

        let entry: KPIRuntime | null = null;
        let entryIndex = 0;
        for (const e of KpiVisualiserContext.entries) {
            if (e.id === KpiVisualiserContext.selectedEntry) {
                entry = e;
                break;
            }

            ++entryIndex;
            if (e.agreedDeadlineDate) {
                ++entryIndex;
            }
        }

        if (!entry) {
            this.returnToCurrentTime();
            return;
        }

        const startDate = dateObjToUtcDate(entry.startDate);
        const actualStartDate = dateObjToUtcDate(entry.actualStartDate);
        const endDate = entry.endDate ? dateObjToUtcDate(entry.endDate) : startDate;

        let targetDate = startDate;
        if (endDate < targetDate) {
            if (actualStartDate.getFullYear() > 2000) {
                targetDate = actualStartDate;
            }

            if (endDate < targetDate) {
                targetDate = endDate;
            }
        }
        this.setState({
            scrollX: -dateToXPos(targetDate, this.state.zoom) + (INITIAL_SCROLL_OFFSET * 0.5),
            scrollY: Math.min(Math.max(this.state.scrollY, -entryIndexToYPos(entryIndex, true) + ENTRY_PADDING * 2), 0),
        });
    }

    returnToFirstRuntime() {
        if (KpiVisualiserContext.entries.length == 0) {
            this.returnToCurrentTime();
            return;
        }

        let targetDate: Date | null = null;

        for (const entry of KpiVisualiserContext.entries) {
            const startDate = dateObjToUtcDate(entry.startDate);
            const actualStartDate = dateObjToUtcDate(entry.actualStartDate);
            const endDate = entry.endDate ? dateObjToUtcDate(entry.endDate) : startDate;

            if (targetDate == null || startDate < targetDate) {
                targetDate = startDate;
            }

            if (endDate < targetDate) {
                if (actualStartDate.getFullYear() > 2000) {
                    targetDate = actualStartDate;
                }

                if (endDate < targetDate) {
                    targetDate = endDate;
                }
            }
        }

        this.setState({
            scrollX: -dateToXPos(targetDate ?? new Date(), this.state.zoom) + (INITIAL_SCROLL_OFFSET * 0.5),
            scrollY: 0,
        });
    }

    returnToCurrentTime() {
        this.setState({
            scrollX: INITIAL_SCROLL_OFFSET,
        })
    }

    deselectSelectedEntry() {
        KpiVisualiserContext.pageReference?.invokeMethodAsync('JS_SelectKPI', null);
    }

    mouseDownPoint: { x: number, y: number } = { x: 0, y: 0 };

    onMouseDown(e: MouseEvent) {
        this.mouseDownPoint.x = e.clientX;
        this.mouseDownPoint.y = e.clientY;

        this.setState({ mouseDown: true });
    }


    onMouseMove(e: MouseEvent) {
        if (this.state.mouseDown) {
            const newState: State = { ...this.state, controlsVisible: true };

            newState.scrollX = this.state.scrollX + (e.clientX - this.mouseDownPoint.x);
            newState.scrollY = Math.min(0, this.state.scrollY + (e.clientY - this.mouseDownPoint.y));

            this.mouseDownPoint.x = e.clientX;
            this.mouseDownPoint.y = e.clientY;

            this.setState(newState);
        }
    }

    onMouseUp(e: MouseEvent) {
        this.setState({ mouseDown: false });
    }

    onMouseLeave(e: MouseEvent) {
        this.onMouseUp(e);
        this.setState({ controlsVisible: false });
    }

    onMouseWheel(e: WheelEvent) {
        const zoom = Math.max(MIN_ZOOM_LEVEL, Math.min(MAX_ZOOM_LEVEL, this.state.zoom - e.deltaY * 0.001));

        this.setState({ zoom });
        e.preventDefault();
    }

    render() {
        const nowDate = new Date();
        const now = nowDate.getTime() - (this.state.scrollX / (DAY_WIDTH * this.state.zoom)) * DAYS_TO_MS;
        const dist = Math.ceil(48 * (1 / (this.state.zoom * 5.0))) * DAYS_TO_MS;
        const startDate = new Date(now - dist);
        const endDate = new Date(now + dist * 3);


        return (
            <ErrorBoundary>
                <div className={styles.kpiVisualiserWrapper} onMouseLeave={e => this.onMouseLeave(e)}>
                    <svg className={styles.kpiVisualiser}
                        onMouseDown={e => this.onMouseDown(e)} onMouseMove={e => this.onMouseMove(e)}
                        onMouseUp={e => this.onMouseUp(e)} onWheel={e => this.onMouseWheel(e)}
                    >
                        <SvgCommonDefs />

                        <g transform={`translate(${this.state.scrollX}, 0)`}>{/* Main content */}
                            <KpiVisualiser_Grid_Lines startDate={startDate} endDate={endDate} zoom={this.state.zoom} scrollX={this.state.scrollX} />

                            <g transform={`translate(0, ${this.state.scrollY})`}>
                                {KpiVisualiserContext.entries.map((e, i) => (
                                    <KpiVisualiser_Entry key={e.id} entry={e} index={i} zoom={this.state.zoom} scrollX={this.state.scrollX}
                                        isSelected={KpiVisualiserContext.selectedEntry === e.id} />
                                ))}
                            </g>

                            <KpiVisualiser_TrackerLine zoom={this.state.zoom} />
                        </g>

                        <g>{/* Left-side runtime list */}
                            <rect x={0} y={0} width={LEFT_HEADER_WIDTH} height={99999} class={styles.leftHeaderContainer} onClick={() => this.deselectSelectedEntry()} />
                            <line x1={LEFT_HEADER_WIDTH} y1={0} x2={LEFT_HEADER_WIDTH} y2={99999} stroke="rgba(0,0,0,0.2)" />

                            <g transform={`translate(0, ${this.state.scrollY})`}>
                                {KpiVisualiserContext.entries.map((e, i) => (
                                    <KpiVisualiser_Entry_LeftHeader key={e.id} entry={e} index={i}
                                        isSelected={KpiVisualiserContext.selectedEntry === e.id} />
                                ))}
                            </g>
                        </g>

                        <rect x={0} y={0} width={99999} height={GRID_MARGIN_TOP} class={styles.topBarContainer} />

                        <g transform={`translate(${this.state.scrollX}, 0)`}>{/* Top bar */}
                            <KpiVisualiser_Grid_Top startDate={startDate} endDate={endDate} zoom={this.state.zoom} scrollX={this.state.scrollX} />
                        </g>
                    </svg>

                    <KpiVisualiser_Key hidden={/* !this.state.controlsVisible || */ !this.state.keyVisible} />

                    <div className={`${styles.zoomControl} ${/*!this.state.controlsVisible ? styles.hidden : */''}`}>
                        <label htmlFor="kpi-visualiser--zoomctrl">Zoom</label>
                        <input type="range" id="kpi-visualiser--zoomctrl" className="form-range" min={MIN_ZOOM_LEVEL} max={MAX_ZOOM_LEVEL} step={0.1} value={this.state.zoom} onInput={e => this.setState({ zoom: e.currentTarget.valueAsNumber })} />
                        <button type="button" className="btn btn-primary" onClick={() => this.setState({ keyVisible: !this.state.keyVisible })}>
                            Toggle Key
                        </button>
                    </div>
                </div>
            </ErrorBoundary>
        );
    }
}

class ErrorBoundary extends Component {
    state = { error: null }

    static getDerivedStateFromError(error) {
        return { error: error.message }
    }

    componentDidCatch(error) {
        console.error(error)
        this.setState({ error: error.message })
    }

    render() {
        if (this.state.error) {
            return (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"></path>
                    </svg>
                    <div className="px-2">
                        <h3 className="mb-0">
                            Error
                        </h3>
                        <p className="mb-0">
                            An error occured within the KPI Visualiser. Please raise a HelpDesk ticket detailing what you were doing when this error occurred and the following error message:
                        </p>
                        <code>{this.state.error}</code>
                    </div>
                </div>
            );
        }
        return this.props.children
    }
}

// The entire point of this class is to act as a React root
export class KPIVisualiserDOM extends HTMLElement {
    constructor() {
        super();

        render(<KpiVisualiser domElement={this} />, this);
    }
}

customElements.define("kpi-visualiser", KPIVisualiserDOM);
(window as any).KPIVisualiser = KpiVisualiser;
